/**
 * FileDropZone Component
 * Wrapper that adds drag-and-drop file handling from File Panel
 * - Highlights on drag over
 * - Accepts file context from File Panel
 * - Stores file references in parent data
 */

import React, { useCallback, useState } from 'react';
import { fileContextService } from '../../services/FileContextService';

export interface FileContext {
  fileId: string;
  fileName: string;
  summary?: string;
  content?: string;
  /**
   * Set when the file's AI context is resolved on demand rather than being carried in
   * the drag payload. `analyzing` while the (billable) analysis runs, `error` if it
   * failed — both are rendered by FileContextIndicator so the wait and the failure are
   * visible instead of the node just sitting there with nothing attached.
   */
  analysisStatus?: 'analyzing' | 'ready' | 'error';
  analysisError?: string;
}

export interface FileDropZoneProps {
  children: React.ReactNode;
  onFilesDropped?: (files: FileContext[]) => void;
  disabled?: boolean;
  className?: string;
}

export const FileDropZone: React.FC<FileDropZoneProps> = ({
  children,
  onFilesDropped,
  disabled = false,
  className = ''
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (disabled) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (disabled) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, [disabled]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    if (disabled) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));

      if (!dragData.fileId || !onFilesDropped) return;

      const fileContext: FileContext = {
        fileId: dragData.fileId,
        fileName: dragData.fileName,
        summary: dragData.context?.summary,
        content: dragData.context?.content
      };

      // Already analysed (document processor, or an earlier on-demand analysis that
      // the panel has in `contexts`) — attach it and we are done, nothing is billed.
      if (fileContext.summary || fileContext.content) {
        onFilesDropped([fileContext]);
        return;
      }

      // Not analysed yet. Attach immediately so the node shows the file and a visible
      // "Analyzing…" state, then resolve the context on demand. The parent upserts by
      // fileId, so the second call replaces this placeholder.
      onFilesDropped([{ ...fileContext, analysisStatus: 'analyzing' }]);

      fileContextService
        .ensureContext({
          fileId: dragData.fileId,
          fileName: dragData.fileName,
          fileType: dragData.fileType
        })
        .then((context) => {
          onFilesDropped([{
            fileId: dragData.fileId,
            fileName: dragData.fileName,
            summary: context.summary,
            content: context.content as unknown as string,
            analysisStatus: 'ready'
          }]);
        })
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : String(err);
          console.error('[FileDropZone] Context analysis failed:', message);
          onFilesDropped([{
            ...fileContext,
            analysisStatus: 'error',
            analysisError: message
          }]);
        });
    } catch (err) {
      console.error('Failed to parse dropped file data:', err);
    }
  }, [disabled, onFilesDropped]);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`file-drop-zone ${isDragOver ? 'drop-zone-active' : ''} ${className}`}
      data-drag-over={isDragOver}
    >
      {children}
    </div>
  );
};
